import { useEffect, useState, useCallback, useContext } from "react";
import { CiCirclePlus, CiSearch } from "react-icons/ci";
import { MdEdit, MdDelete, MdQrCode2 } from "react-icons/md";
import toast from "react-hot-toast";
import Loader from "../../components/Loader";
import { AuthContext } from "../../context/AuthContext";
import NoData from "../../components/NoData";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import PaymentAccountModal, {
  PAYMENT_METHODS,
} from "./modals/PaymentAccountModal";
import PaymentAccountServices, {
  paymentQrUrl,
} from "../../services/PaymentAccountServices";
import "../../styles/paymentAccount.css";

const methodLabel = (value) =>
  PAYMENT_METHODS.find((m) => m.value === value)?.label || value;

/** The identifier a user actually pays to, which differs per method. */
const accountDetail = (row) => {
  if (row.method === "UPI") return row.upi_id || "-";
  if (row.method === "PAYTM" || row.method === "PHONEPE")
    return row.mobile_number || "-";
  return row.qr_image ? "QR image uploaded" : "-";
};

const PaymentAccountManage = () => {
  const paymentApi = new PaymentAccountServices();
  const { user } = useContext(AuthContext);
  // Super Admin sees every account with its owner; an Admin only their own.
  const isSuperAdmin = Number(user?.role_id) === 1;

  const [loading, setLoading] = useState(false);
  const [accountList, setAccountList] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");

  const [modalIsOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [togglingId, setTogglingId] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);

  const getPaymentAccountList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await paymentApi.getPaymentAccountList();
      if (response.isSuccess) {
        setAccountList(response.data || []);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to load payment accounts");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getPaymentAccountList();
  }, [getPaymentAccountList]);

  const handleAdd = () => {
    setEditData(null);
    setIsOpen(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setIsOpen(true);
  };

  const handleToggle = async (row) => {
    const next = row.is_active ? 0 : 1;
    setTogglingId(row.id);
    try {
      const response = await paymentApi.togglePaymentAccountStatus(
        row.id,
        next,
      );
      if (response.isSuccess) {
        toast.success(response.message);
        getPaymentAccountList();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteClick = (row) => {
    setSelectedAccount(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAccount) return;
    setDeleteLoading(true);
    try {
      const response = await paymentApi.deletePaymentAccount(
        selectedAccount.id,
      );
      if (response.isSuccess) {
        toast.success(response.message);
        setDeleteModalOpen(false);
        setSelectedAccount(null);
        getPaymentAccountList();
      } else {
        // Server blocks deletion while pending deposits reference the account.
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to delete payment account",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredList = accountList.filter((row) => {
    const matchesMethod = !methodFilter || row.method === methodFilter;
    const term = searchFilter.trim().toLowerCase();
    const matchesSearch =
      !term ||
      String(row.display_name || "").toLowerCase().includes(term) ||
      String(row.upi_id || "").toLowerCase().includes(term) ||
      String(row.mobile_number || "").toLowerCase().includes(term);
    return matchesMethod && matchesSearch;
  });

  const activeCount = accountList.filter((r) => r.is_active).length;

  return (
    <div className="payment-account-page">
      <div className="pa-header">
        <div>
          <h2 className="pa-title">Payment Accounts</h2>
          <p className="pa-subtitle">
            {activeCount} active of {accountList.length} total.{" "}
            {isSuperAdmin
              ? "Players pay into their Admin's accounts; these are shown when the Admin has none."
              : "Your players see only active accounts on their deposit screen."}
          </p>
        </div>
        <button className="btn btn-primary pa-add-btn" onClick={handleAdd}>
          <CiCirclePlus size={20} /> Add Account
        </button>
      </div>

      <div className="pa-filters">
        <div className="pa-search">
          <CiSearch size={18} />
          <input
            type="text"
            placeholder="Search by name, UPI ID or mobile"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>
        <select
          className="form-select pa-method-filter"
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
        >
          <option value="">All Methods</option>
          {PAYMENT_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <Loader message="Loading payment accounts..." />
      ) : filteredList.length === 0 ? (
        <NoData
          message="No Payment Accounts"
          subText="Add a UPI, QR, Paytm or PhonePe account to accept deposits"
        />
      ) : (
        <div className="pa-table-wrapper">
          <table className="pa-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Method</th>
                <th>Display Name</th>
                <th>Account Detail</th>
                {isSuperAdmin && <th>Owner</th>}
                <th>Sort</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((row, index) => (
                <tr key={row.id} className={row.is_active ? "" : "pa-inactive"}>
                  <td>{index + 1}</td>
                  <td>
                    <span className={`pa-badge pa-badge-${row.method.toLowerCase()}`}>
                      {methodLabel(row.method)}
                    </span>
                  </td>
                  <td>{row.display_name}</td>
                  <td>
                    {row.method === "QR" && row.qr_image ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 pa-qr-link"
                        onClick={() => setQrPreview(paymentQrUrl(row.qr_image))}
                      >
                        <MdQrCode2 size={16} /> View QR
                      </button>
                    ) : (
                      accountDetail(row)
                    )}
                  </td>
                  {isSuperAdmin && (
                    <td>
                      {Number(row.created_by_role_id) === 1 || !row.created_by ? (
                        <span className="pa-owner pa-owner-super">Super Admin</span>
                      ) : (
                        <span className="pa-owner">
                          {row.created_by_name || row.created_by_username || "—"}
                        </span>
                      )}
                    </td>
                  )}
                  <td>{row.sort_order}</td>
                  <td>
                    <label className="pa-switch">
                      <input
                        type="checkbox"
                        checked={Boolean(row.is_active)}
                        disabled={togglingId === row.id}
                        onChange={() => handleToggle(row)}
                      />
                      <span className="pa-slider" />
                    </label>
                    <span
                      className={`pa-status ${row.is_active ? "on" : "off"}`}
                    >
                      {row.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm pa-icon-btn"
                      title="Edit"
                      onClick={() => handleEdit(row)}
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      className="btn btn-sm pa-icon-btn pa-icon-danger"
                      title="Delete"
                      onClick={() => handleDeleteClick(row)}
                    >
                      <MdDelete size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalIsOpen && (
        <PaymentAccountModal
          modalIsOpen={modalIsOpen}
          setIsOpen={setIsOpen}
          editData={editData}
          getPaymentAccountList={getPaymentAccountList}
        />
      )}

      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Payment Account"
        message={`Delete "${selectedAccount?.display_name || ""}"? Users will no longer see this account on the deposit screen.`}
        loading={deleteLoading}
      />

      {qrPreview && (
        <div className="pa-qr-overlay" onClick={() => setQrPreview(null)}>
          <img src={qrPreview} alt="QR code" />
        </div>
      )}
    </div>
  );
};

export default PaymentAccountManage;
